<ng-container *ngIf="compact; else card">
  <div data-testid="eligibility-compact" class="flex flex-col gap-2 mt-2">
    <div class="flex items-center gap-2 flex-wrap">
      <p class="text-sm text-gray-500">{{ config.title }}</p>

      <ng-container *ngIf="latest as latest">
        <vs-badge [theme]="themes[latest.outcome]" [config]="{ displayStatusIcon: true }">
          {{ config.outcomes[latest.outcome] }}
        </vs-badge>
        <p *ngIf="latest.limits as limits" class="text-sm text-gray-500">
          · {{ limits.perClaim ? config.limits.perClaim : config.limits.remaining }}
          {{ formatAmount(limits.remainingLimit) }}
        </p>
      </ng-container>

      <button
        *ngIf="canCheck && results"
        vs-button
        size="sm"
        appearance="outline"
        class="ml-auto"
        type="button"
        data-testid="eligibility-check"
        [disabled]="checking"
        (click)="check()"
      >
        {{ checking ? config.checking : latest ? config.checkAgain : config.check }}
      </button>
    </div>

    <p *ngIf="loadFailed" class="text-sm text-danger-600">{{ config.loadFailed }}</p>
    <p *ngIf="problem as problem" data-testid="eligibility-problem" class="text-sm text-warn-700">
      {{ approvalText(problem) }}
    </p>
    <p *ngIf="blockingFinding as finding" data-testid="eligibility-reason" class="text-sm text-gray-600">
      {{ finding.message }}
      <ng-container *ngIf="hasValues(finding)">
        · {{ config.expected }} {{ operatorText(finding.operator) }} {{ formatValue(finding, finding.expected) }} ·
        {{ config.found }} {{ formatValue(finding, finding.actual) }}
      </ng-container>
    </p>
  </div>
</ng-container>

<ng-template #card>
  <div data-testid="eligibility-card" class="flex flex-col gap-4 border rounded-xl p-6">
    <div class="flex items-center gap-2 flex-wrap">
      <p class="text-sm font-medium text-gray-700">{{ config.title }}</p>

      <ng-container *ngIf="latest as latest">
        <vs-badge [theme]="themes[latest.outcome]" [config]="{ displayStatusIcon: true }">
          {{ config.outcomes[latest.outcome] }}
        </vs-badge>
        <p class="text-sm text-gray-500">{{ config.version }} {{ latest.version }}</p>
      </ng-container>

      <button
        *ngIf="canCheck && results"
        vs-button
        size="sm"
        appearance="outline"
        class="ml-auto"
        type="button"
        data-testid="eligibility-check"
        [disabled]="checking"
        (click)="check()"
      >
        {{ checking ? config.checking : latest ? config.checkAgain : config.check }}
      </button>
    </div>

    <p *ngIf="loadFailed" class="text-sm text-danger-600">{{ config.loadFailed }}</p>
    <p *ngIf="results && !latest" class="text-sm text-gray-600">{{ config.notChecked }}</p>

    <ng-container *ngIf="latest as latest">
      <p class="text-sm text-gray-500">
        {{ config.checkedBy(latest.createdBy?.displayName || config.someone) }} · {{ latest.created | date: 'medium' }}
      </p>

      <p *ngIf="changed.length" data-testid="eligibility-out-of-date" class="text-sm text-warn-700">
        {{ config.outOfDate }}
      </p>

      <vs-divider></vs-divider>

      <ng-container
        *ngTemplateOutlet="findingsList; context: { $implicit: latest, testId: 'eligibility-findings' }"
      ></ng-container>

      <p
        *ngIf="approvedLine as line"
        data-testid="eligibility-approved"
        class="text-sm"
        [class.text-success-700]="line.within"
        [class.text-warn-700]="!line.within"
      >
        {{ line.text }}
      </p>

      <ng-container *ngIf="latest.limits as limits">
        <vs-divider></vs-divider>

        <dl data-testid="eligibility-limits" class="flex flex-col gap-1 text-sm">
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500">{{ limits.perClaim ? config.limits.perClaim : config.limits.remaining }}</dt>
            <dd class="text-gray-700">
              {{ formatAmount(limits.remainingLimit) }}
              <span *ngIf="limits.bindingLimit === 'GROUP'" class="text-gray-500">({{ config.limits.group }})</span>
            </dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500">{{ config.limits.requested }}</dt>
            <dd class="text-gray-700">{{ formatAmount(limits.requestedAmount) }}</dd>
          </div>
          <div class="flex justify-between gap-4">
            <dt class="text-gray-500">{{ config.limits.deductible }}</dt>
            <dd class="text-gray-700">{{ formatAmount(limits.deductible) }}</dd>
          </div>
          <div *ngIf="!limits.perClaim" class="flex justify-between gap-4">
            <dt class="text-gray-500">{{ config.limits.prior }}</dt>
            <dd class="text-gray-700">{{ formatAmount(limits.priorResolvedAmount) }}</dd>
          </div>
        </dl>
      </ng-container>

      <ng-container *ngIf="latest.usage?.length">
        <vs-divider></vs-divider>
        <ng-container
          *ngTemplateOutlet="usageList; context: { $implicit: latest, testId: 'eligibility-usage' }"
        ></ng-container>
      </ng-container>

      <ng-container *ngIf="earlier.length">
        <vs-divider></vs-divider>

        <button
          vs-button
          appearance="clear"
          size="sm"
          type="button"
          class="self-start"
          data-testid="eligibility-toggle-earlier"
          (click)="showEarlier = !showEarlier"
        >
          {{ showEarlier ? config.hideEarlier : config.showEarlier(earlier.length) }}
        </button>

        <ul *ngIf="showEarlier" data-testid="eligibility-earlier" class="flex flex-col gap-2">
          <li *ngFor="let result of earlier" class="flex flex-col gap-2">
            <button
              type="button"
              class="flex items-center gap-2 flex-wrap w-full text-left"
              data-testid="eligibility-earlier-toggle"
              [attr.aria-expanded]="isExpanded(result)"
              [attr.aria-label]="isExpanded(result) ? config.earlierDetails.collapse : config.earlierDetails.expand"
              (click)="toggleEarlier(result)"
            >
              <vs-icon class="text-gray-500" [name]="isExpanded(result) ? 'chevron-up' : 'chevron-down'"></vs-icon>
              <vs-badge [theme]="themes[result.outcome]">{{ config.outcomes[result.outcome] }}</vs-badge>
              <p class="text-sm text-gray-500">{{ config.version }} {{ result.version }}</p>
              <p class="text-sm text-gray-500 ml-auto">
                {{ result.createdBy?.displayName || config.someone }} · {{ result.created | date: 'medium' }}
              </p>
            </button>

            <div
              *ngIf="isExpanded(result)"
              data-testid="eligibility-earlier-details"
              class="flex flex-col gap-2 ml-2 pl-4 border-l"
            >
              <!-- A plain 'YYYY-MM-DD' day is read as local midnight, so no time zone here -->
              <p class="text-sm text-gray-500">
                {{ config.earlierDetails.lossDate }}
                {{ result.inputs.lossDate ? (result.inputs.lossDate | date: 'mediumDate') : '-' }}
              </p>
              <ng-container
                *ngTemplateOutlet="findingsList; context: { $implicit: result, testId: null }"
              ></ng-container>
              <ng-container *ngIf="result.usage?.length">
                <ng-container
                  *ngTemplateOutlet="usageList; context: { $implicit: result, testId: null }"
                ></ng-container>
              </ng-container>
            </div>
          </li>
        </ul>
      </ng-container>
    </ng-container>
  </div>
</ng-template>

<!-- Why a check came out as it did; shared by the latest check and the earlier ones. -->
<ng-template #findingsList let-result let-testId="testId">
  <p *ngIf="!result.findings.length" class="text-sm text-gray-600">{{ config.noFindings }}</p>
  <ul *ngIf="result.findings.length" class="flex flex-col gap-2" [attr.data-testid]="testId">
    <li *ngFor="let finding of result.findings" class="flex flex-col">
      <p class="text-sm text-gray-700">{{ finding.message }}</p>
      <p *ngIf="hasValues(finding)" class="text-sm text-gray-500">
        {{ config.expected }} {{ operatorText(finding.operator) }} {{ formatValue(finding, finding.expected) }} ·
        {{ config.found }} {{ formatValue(finding, finding.actual) }}
      </p>
    </li>
  </ul>
</ng-template>

<ng-template #usageList let-result let-testId="testId">
  <ul class="flex flex-col gap-1" [attr.data-testid]="testId">
    <li *ngFor="let usage of result.usage" class="flex justify-between gap-4 text-sm">
      <p class="text-gray-500">{{ usage.label }}</p>
      <p class="text-gray-700">
        {{
          measuredUse(usage) === null
            ? config.usage.limitOnly(formatNumber(usage.limit))
            : config.usage.summary(formatNumber(measuredUse(usage)), formatNumber(usage.limit))
        }}
        ·
        {{ usageStatus(usage) }}
      </p>
    </li>
  </ul>
</ng-template>
